import { useState, useEffect } from "react";
import { MessageCircle, ArrowDownRight, Globe, Target, Linkedin, Sparkles, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const Hero = () => {
  // Base stats - 4-week campaigns across clients
  const baseStats = {
    totalLeads: 13597,
    avgAcceptance: 56,
    qualifiedLeads: 487,
    bookedCalls: 107,
    campaignDuration: "4 weeks",
  };

  // Animated counters for live growth effect
  const [animatedLeads, setAnimatedLeads] = useState(baseStats.totalLeads);
  const [animatedQualified, setAnimatedQualified] = useState(baseStats.qualifiedLeads);
  const [animatedCalls, setAnimatedCalls] = useState(baseStats.bookedCalls);

  useEffect(() => {
    // Leads scraped: +1-23 (odd numbers) every 20 seconds - odd numbers feel more authentic
    const leadsInterval = setInterval(() => {
      const randomOdd = Math.floor(Math.random() * 12) * 2 + 1; // Generates 1,3,5,7,9,11,13,15,17,19,21,23
      setAnimatedLeads(prev => prev + randomOdd);
    }, 20000);

    // Qualified leads: +1,3,5 (odd) every 21 seconds (~4 per day accelerated)
    const qualifiedInterval = setInterval(() => {
      const randomOdd = Math.floor(Math.random() * 3) * 2 + 1; // Generates 1, 3, or 5
      setAnimatedQualified(prev => prev + randomOdd);
    }, 21000);

    // Booked calls: +1 or 3 (odd) every 86 seconds (~1 per day accelerated)
    const callsInterval = setInterval(() => {
      const randomOdd = Math.random() > 0.7 ? 3 : 1; // 70% chance of +1, 30% chance of +3
      setAnimatedCalls(prev => prev + randomOdd);
    }, 86000);

    return () => {
      clearInterval(leadsInterval);
      clearInterval(qualifiedInterval);
      clearInterval(callsInterval);
    };
  }, []);

  const serviceButtons = [
    {
      id: "website",
      title: "Website Design",
      description: "Want a site this good?",
      icon: Globe,
      link: "/website-design",
      color: "from-blue-500/20 to-blue-600/30",
      borderColor: "border-blue-400/30",
    },
    {
      id: "paid-ads",
      title: "Paid Ads",
      description: "Scalable growth campaigns",
      icon: Target,
      link: "/paid-ads",
      color: "from-orange-500/30 to-amber-500/40",
      borderColor: "border-orange-400/50",
      isHighlighted: true,
      badge: "🔥 High ROI",
    },
    {
      id: "linkedin",
      title: "LinkedIn SDR",
      description: "B2B lead generation",
      icon: Linkedin,
      link: "/linkedin-sdr",
      color: "from-[#0A66C2]/30 to-[#0077B5]/50",
      borderColor: "border-[#0A66C2]/60",
      isHighlighted: true,
      badge: "🔥 High ROI",
    },
    {
      id: "other",
      title: "Other Marketing",
      description: "Custom solutions for you",
      icon: Sparkles,
      link: "/book-call",
      color: "from-secondary/20 to-secondary/40",
      borderColor: "border-secondary/30",
    },
  ];

  return (
    <section className="relative py-8 md:py-16 lg:py-32 overflow-hidden" style={{ background: 'var(--gradient-hero)' }}>
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 -left-4 w-72 h-72 bg-secondary rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
        <div className="absolute top-0 -right-4 w-72 h-72 bg-accent rounded-full mix-blend-multiply filter blur-xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-secondary/50 rounded-full mix-blend-multiply filter blur-xl animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>
      
      {/* Gradient overlay for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-primary/20"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Heading - Always First */}
          <div className="text-center lg:text-left mb-4 lg:mb-0">
            <h1 className="text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-2 md:mb-6 text-white leading-tight">
              Grow Through Lead Gen or Smart Websites. We Can Do Both.
            </h1>
            <p className="text-sm md:text-xl lg:text-2xl mb-4 md:mb-8 text-white/90 leading-relaxed">
              Stop guessing. Get a predictable system that turns visitors into high-value clients.
            </p>
          </div>

          {/* Desktop: Two Column Layout | Mobile: Stacked */}
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            {/* Left Column - Service Buttons (Hidden on mobile, shown on desktop) */}
            <div className="hidden lg:block text-center lg:text-left">
              {/* Service Buttons Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                {serviceButtons.map((service) => {
                  const IconComponent = service.icon;
                  
                  if (service.id === "linkedin") {
                    return (
                      <div key={service.id} className="col-span-2">
                        <Link
                          to="/linkedin-sdr"
                          className={`block w-full bg-gradient-to-br ${service.color} backdrop-blur-sm rounded-xl p-5 border-2 ${service.borderColor} hover:scale-[1.02] transition-all duration-300 cursor-pointer group text-left relative overflow-hidden shadow-lg shadow-[#0A66C2]/20`}
                        >
                          {/* Highlight glow effect */}
                          <div className="absolute inset-0 bg-gradient-to-r from-[#0A66C2]/20 via-[#0077B5]/30 to-[#0A66C2]/20 animate-pulse"></div>
                          
                          {/* High ROI Badge */}
                          <div className="absolute -top-1 -right-1 z-20">
                            <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg animate-pulse">
                              {service.badge}
                            </span>
                          </div>
                          
                          <div className="relative z-10">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 rounded-lg">
                                  <IconComponent className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                  <p className="text-lg font-bold text-white">{service.title}</p>
                                  <p className="text-white/70 text-sm">{service.description}</p>
                                </div>
                              </div>
                              <ArrowRight className="w-5 h-5 text-white/70 group-hover:translate-x-1 transition-transform" />
                            </div>
                            
                            {/* LinkedIn Stats Showcase - Key Metrics with standout conversion */}
                            <div className="grid grid-cols-4 gap-2 mt-3">
                              <div className="bg-white/15 rounded-lg p-3 text-center border border-white/10">
                                <span className="text-xl font-bold text-white transition-all duration-300">{animatedLeads.toLocaleString()}</span>
                                <p className="text-white/80 text-xs font-medium">Leads Scraped</p>
                              </div>
                              <div className="bg-white/15 rounded-lg p-3 text-center border border-white/10">
                                <span className="text-xl font-bold text-green-400">{baseStats.avgAcceptance}%</span>
                                <p className="text-white/80 text-xs font-medium">Accept Rate</p>
                              </div>
                              <div className="bg-white/15 rounded-lg p-3 text-center border border-white/10">
                                <span className="text-xl font-bold text-white transition-all duration-300">{animatedQualified}</span>
                                <p className="text-white/80 text-xs font-medium">Qualified</p>
                              </div>
                              {/* STANDOUT: Booked Calls - This is the money stat! */}
                              <div className="bg-gradient-to-br from-green-500/30 to-emerald-500/40 rounded-lg p-3 text-center border-2 border-green-400/50 shadow-lg shadow-green-500/20 relative overflow-hidden">
                                <div className="absolute inset-0 bg-green-400/10 animate-pulse"></div>
                                <div className="relative z-10 flex flex-col items-center">
                                  <span className="text-2xl font-black text-green-400 transition-all duration-300">{animatedCalls}+</span>
                                  <p className="text-white text-xs font-bold">Calls Booked</p>
                                  <span className="mt-1 text-[9px] text-green-300 font-semibold animate-pulse">& counting</span>
                                </div>
                              </div>
                            </div>
                            
                            <p className="text-white/70 text-sm mt-3 text-center font-medium">
                              {baseStats.campaignDuration} campaigns • Click for case studies →
                            </p>
                          </div>
                        </Link>
                      </div>
                    );
                  }
                  
                  // Special rendering for Paid Ads
                  if (service.id === "paid-ads") {
                    return (
                      <Link
                        key={service.id}
                        to={service.link || "/"}
                        className={`relative bg-gradient-to-br ${service.color} backdrop-blur-sm rounded-xl p-4 border-2 ${service.borderColor} hover:scale-105 transition-all duration-300 cursor-pointer group overflow-hidden`}
                      >
                        {/* Animated glow effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-amber-400/30 to-orange-500/20 animate-pulse"></div>
                        <div className="absolute -top-1 -right-1">
                          <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg">
                            {service.badge}
                          </span>
                        </div>
                        <div className="relative z-10">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-orange-500/30 rounded-lg group-hover:bg-orange-500/50 transition-colors">
                              <IconComponent className="w-5 h-5 text-white" />
                            </div>
                            <ArrowRight className="w-4 h-4 text-white/50 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all ml-auto" />
                          </div>
                          <p className="text-white font-bold text-sm md:text-base">{service.title}</p>
                          <p className="text-white/70 text-xs">{service.description}</p>
                        </div>
                      </Link>
                    );
                  }

                  return (
                    <Link
                      key={service.id}
                      to={service.link || "/"}
                      className={`bg-gradient-to-br ${service.color} backdrop-blur-sm rounded-xl p-4 border ${service.borderColor} hover:scale-105 transition-all duration-300 cursor-pointer group`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-white/10 rounded-lg group-hover:bg-white/20 transition-colors">
                          <IconComponent className="w-5 h-5 text-white" />
                        </div>
                        <ArrowRight className="w-4 h-4 text-white/50 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all ml-auto" />
                      </div>
                      <p className="text-white font-bold text-sm md:text-base">{service.title}</p>
                      <p className="text-white/70 text-xs">{service.description}</p>
                    </Link>
                  );
                })}
              </div>

              <p className="text-white/80 text-sm md:text-base">
                Trusted by premium businesses who demand results, not excuses.
              </p>
            </div>

            {/* Mobile Service Buttons */}
            <div className="lg:hidden grid grid-cols-2 gap-3 mb-6">
              {serviceButtons.filter(s => s.id !== "linkedin").map((service) => {
                const IconComponent = service.icon;
                
                // Special rendering for Paid Ads on mobile
                if (service.id === "paid-ads") {
                  return (
                    <Link
                      key={service.id}
                      to={service.link || "/"}
                      className={`relative bg-gradient-to-br ${service.color} backdrop-blur-sm rounded-xl p-3 border-2 ${service.borderColor} active:scale-95 transition-all overflow-hidden`}
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 via-amber-400/30 to-orange-500/20 animate-pulse"></div>
                      <div className="absolute -top-0.5 -right-0.5">
                        <span className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full">
                          🔥
                        </span>
                      </div>
                      <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-1">
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <p className="text-white font-bold text-xs">{service.title}</p>
                      </div>
                    </Link>
                  );
                }
                
                return (
                  <Link
                    key={service.id}
                    to={service.link || "/"}
                    className={`bg-gradient-to-br ${service.color} backdrop-blur-sm rounded-xl p-3 border ${service.borderColor} active:scale-95 transition-all`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <IconComponent className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-white font-bold text-xs">{service.title}</p>
                  </Link>
                );
              })}
              
              {/* Mobile LinkedIn SDR - Full Width with High ROI badge */}
              <Link to="/linkedin-sdr" className="col-span-2">
                <div className="relative w-full bg-gradient-to-br from-[#0A66C2]/30 to-[#0077B5]/50 backdrop-blur-sm rounded-xl p-4 border-2 border-[#0A66C2]/60 text-left shadow-lg shadow-[#0A66C2]/20 overflow-hidden">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A66C2]/10 via-[#0077B5]/20 to-[#0A66C2]/10 animate-pulse"></div>
                  
                  {/* High ROI Badge */}
                  <div className="absolute -top-0.5 -right-0.5 z-20">
                    <span className="bg-gradient-to-r from-green-500 to-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg">
                      🔥 High ROI
                    </span>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Linkedin className="w-5 h-5 text-white" />
                        <span className="text-white font-bold text-sm">LinkedIn SDR</span>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/70" />
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <span className="text-base font-bold text-white transition-all duration-300">{(animatedLeads / 1000).toFixed(0)}k</span>
                        <p className="text-white/70 text-[10px]">Scraped</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <span className="text-base font-bold text-green-400">{baseStats.avgAcceptance}%</span>
                        <p className="text-white/70 text-[10px]">Accept</p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-2 text-center">
                        <span className="text-base font-bold text-white transition-all duration-300">{animatedQualified}</span>
                        <p className="text-white/70 text-[10px]">Qualified</p>
                      </div>
                      {/* Standout booked calls */}
                      <div className="bg-gradient-to-br from-green-500/30 to-emerald-500/40 rounded-lg p-2 text-center border border-green-400/50 relative">
                        <span className="text-lg font-black text-green-400 transition-all duration-300">{animatedCalls}+</span>
                        <p className="text-white text-[10px] font-bold">Calls</p>
                        <span className="text-[8px] text-green-300 font-semibold animate-pulse">& counting</span>
                      </div>
                    </div>
                    <p className="text-white/60 text-xs mt-2 text-center">
                      {baseStats.campaignDuration} campaigns • Case studies →
                    </p>
                  </div>
                </div>
              </Link>
            </div>

            {/* Right Column - CTA Card */}
            <div className="flex justify-center">
              <div className="w-full max-w-md lg:max-w-lg">
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 md:p-10 border border-white/20 shadow-2xl">
                  <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary/30 rounded-full mb-6 animate-pulse">
                      <MessageCircle className="w-8 h-8 text-secondary" />
                    </div>
                    
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                      Serious About Growth?
                    </h2>
                    
                    <p className="text-white/90 text-lg md:text-xl mb-6 leading-relaxed">
                      Tell us about your business through our chatbot, and we'll reach out with a tailored strategy within 24 hours.
                    </p>
                    
                    {/* Arrow pointing to GHL chatbot */}
                    <div className="flex items-center justify-center gap-4 text-white pt-6">
                      <span className="text-xl font-semibold">Click the chat icon to get started</span>
                      <div className="relative">
                        <div className="absolute inset-0 bg-secondary rounded-full blur-lg opacity-60 animate-pulse scale-150"></div>
                        <div className="absolute inset-0 bg-white/30 rounded-full blur-md animate-ping"></div>
                        <ArrowDownRight className="w-12 h-12 animate-bounce text-white drop-shadow-[0_0_10px_rgba(184,140,60,0.8)] relative z-10" strokeWidth={3} />
                      </div>
                    </div>
                    
                    <p className="text-white/60 text-sm mt-4">
                      No spam. No pressure. Just results-focused advice.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;